import {
  defaultMarkdownSerializer,
  MarkdownSerializer
} from 'prosemirror-markdown'

// state.esc now also escapes "_", which a URL doesn't need
const escapeUrl = url => url.replace(/[`*\\~[\]]/g, '\\$&')

// state.esc only escapes the block start; a line after a soft break can open a list, quote or heading too
const escapeLineStart = (line, afterLine) => {
  const escaped = line
    .replace(/^( {0,3})(-|>|\+(?=\s|$))/, '$1\\$2')
    .replace(/^( {0,3})(#{1,6})(\s|$)/, '$1\\$2$3')
    .replace(/^( {0,3}\d+)([.)])(\s|$)/, '$1\\$2$3')

  // "===" only underlines the line above it
  return afterLine ? escaped.replace(/^( {0,3})(=+\s*)$/, '$1\\$2') : escaped
}

const escapeText = (state, text) => {
  // A blank marked text after the break may already have written the quote's "> " for the line
  const tail = state.out.slice(-80).replace(/[ \t]+$/, '')
  const delim = state.delim.trimEnd()
  const afterBreak = tail.endsWith('\\\n') || (delim !== '' && tail.endsWith(`\\\n${delim}`))

  return text
    .split('\n')
    .map((line, index) => {
      const escaped = state.esc(line)
      const afterLine = afterBreak || index > 0
      return afterLine || state.atBlockStart ? escapeLineStart(escaped, afterLine) : escaped
    })
    .join('\n')
}

export const markdownSerializer = new MarkdownSerializer(
  {
    ...defaultMarkdownSerializer.nodes,
    image: (state, node) => {
      const { align } = node.attrs
      let alignClass = ''

      if (align !== 'img-align-left') {
        alignClass = align
      }

      state.write(
        '![' +
        state.esc(node.attrs.alt || '') +
        '](' +
        // Need to remove any existing alignment
        escapeUrl(node.attrs.src.replace(/##.*$/gm, '') + `##${alignClass}`) +
        (node.attrs.title ? ' ' + state.quote(node.attrs.title) : '') +
        ')'
      )
    },
    audio: (state, node) => {
      const { insertEscape, src } = node.attrs
      state.write(`${insertEscape ? '\u200B' : ''}[?]`)
      state.write(`(${src})`)
    },
    video: (state, node) => {
      const { insertEscape, src } = node.attrs
      state.write(`${insertEscape ? '\u200B' : ''}[?]`)
      state.write(`(${src})`)
    },
    paragraph: (state, node) => {
      const { align } = node.attrs
      if (align && !align.endsWith('left')) {
        state.write(`~~ ${align} ~~`)
        state.renderInline(node)
        state.closeBlock(node)
      } else {
        state.renderInline(node)
        state.closeBlock(node)
      }
    },
    info: (state, node) => {
      const { href, insertEscape } = node.attrs
      // See align scheme for why we can't have just white space
      state.write(`${insertEscape ? '\u200B' : ''}[?]`)
      state.write(`(${href})`)
    },
    text: (state, node) => {
      if (state.inAutolink) {
        state.text(node.text, false)
      } else {
        state.text(escapeText(state, node.text), false)
      }
    },
    reference: (state, node) => {
      const { href, insertEscape } = node.attrs
      // See align scheme for why we can't have just white space
      state.write(`${insertEscape ? '\u200B' : ''}[?]`)
      state.write(`(${href})`)
    },
  },
  {
    ...defaultMarkdownSerializer.marks,
    itemlink: {
      open: () => {
        return '['
      },
      mixable:                  true,
      expelEnclosingWhitespace: false,
      close:                    (node, mark) => {
        const { href } = mark.attrs
        return `](${href})`
      },
    },
    tag: {
      open: () => {
        return '['
      },
      mixable:                  true,
      expelEnclosingWhitespace: false,
      close:                    (node, mark) => {
        const { href } = mark.attrs
        return `](${href})`
      },
    },
    link: {
      ...defaultMarkdownSerializer.marks.link,
      mixable: true,
    },
  }
)
